// Modal layers of the shell: the news preview, the side sheet, the Orca panel
// and the drawer. Every open layer is on one stack, so there is always one
// clear top layer:
// • Esc and a click on the dimmed page close only the top layer;
// • focus returns to the element that opened a layer when it closes;
// • the page under any open layer doesn't scroll;
// • only ONE scrim is ever visible — the lowest layer's. Orca opened from a
//   detail view (or a sheet opened over the news) reuses the scrim below it
//   (CSS: 04-ask-bar / 18-mock-ux); the global Orca bar hides while a detail
//   view is open, because the detail has its own «Спросить Orca».
import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { subscribeViewportSettled } from './visualViewport';

export type LayerId = 'news' | 'sheet' | 'orca' | 'drawer';
type Layer = { id: LayerId; close: () => void };
let stack: Layer[] = [];
const subs = new Set<() => void>();
// Page scroll under an open layer. Touch screens: the document is never touched
// (no overflow / position / size change on html or body — on iOS that changes the
// visual viewport and the page stops reaching under Safari's toolbar); a touch
// guard stops the page from scrolling instead, while the layer's own scroll areas
// (the drawer menu, a sheet, Orca's thread) scroll as usual. Mouse/trackpad
// screens keep the overflow lock (html.lock-scroll, desktop media only).
let touchY = 0, touchX = 0;
const onTouchStart = (e: TouchEvent) => { touchY = e.touches[0]?.clientY ?? 0; touchX = e.touches[0]?.clientX ?? 0; };
const scrollsHere = (start: EventTarget | null, dx: number, dy: number) => {
  for (let el = start instanceof Element ? start : null; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
    if (!(el instanceof HTMLElement)) continue;
    const s = getComputedStyle(el);
    if (Math.abs(dx) > Math.abs(dy)) {
      if (/(auto|scroll)/.test(s.overflowX) && el.scrollWidth > el.clientWidth + 1) return true;
    } else if (/(auto|scroll)/.test(s.overflowY) && el.scrollHeight > el.clientHeight + 1) {
      const top = el.scrollTop <= 0, bottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      return (dy > 0 && !top) || (dy < 0 && !bottom);
    }
  }
  return false;
};
const onTouchMove = (e: TouchEvent) => {
  if ((!stack.length && !rooms) || e.touches.length > 1) return; // pinch-zoom stays the user's
  const t = e.touches[0];
  if (!scrollsHere(e.target, (t?.clientX ?? 0) - touchX, (t?.clientY ?? 0) - touchY)) e.preventDefault();
};
// The page's home position while it is held still (an open layer: where it was
// opened; the Orca AI room: the top). iOS scrolls the page itself to show a focused
// field above the keyboard and can leave it there once the keyboard closes — the
// page returns home after the shared Visual Viewport controller has settled.
let guarded = false, rooms = 0, homeY = 0, restoreRaf = 0, restoreFrames = 0;
const restoreHome = () => {
  restoreRaf = 0;
  if (!guarded || document.documentElement.classList.contains('app-keyboard-open')) return;
  const off = Math.abs(window.scrollY - homeY);
  if (off >= 1) { window.scrollTo({ left: 0, top: homeY, behavior: 'instant' as ScrollBehavior }); restoreFrames = 0; }
  else restoreFrames += 1;
  if (restoreFrames < 3) restoreRaf = requestAnimationFrame(restoreHome);
};
const settleSoon = () => {
  restoreFrames = 0;
  if (!restoreRaf) restoreRaf = requestAnimationFrame(restoreHome);
};
let stopViewportSettle: (() => void) | null = null;
const guard = (on: boolean) => {
  if (on && !guarded) {
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('scroll', settleSoon, { passive: true });
    document.addEventListener('focusout', settleSoon);
    stopViewportSettle = subscribeViewportSettled(() => settleSoon());
  } else if (!on && guarded) {
    document.removeEventListener('touchstart', onTouchStart);
    document.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('scroll', settleSoon);
    document.removeEventListener('focusout', settleSoon);
    stopViewportSettle?.();
    stopViewportSettle = null;
    if (restoreRaf) cancelAnimationFrame(restoreRaf);
    restoreRaf = 0;
  }
  guarded = on;
};
// Phones (CSS 26-mobile-shell): while a layer is open, its full-height surfaces
// (drawer, scrims, the contextual Orca sheet) are anchored IN the page at the
// current scroll offset instead of being position:fixed — iOS Safari draws page
// content under its floating toolbar, but stops fixed layers above it and leaves
// a black strip. The page can't scroll while a layer is open, so they look the
// same. html.ov-doc stays a moment after the last layer closes, for its exit.
let wasOpen = false, undock = 0;
const dock = (open: boolean) => {
  const root = document.documentElement;
  if (open && !wasOpen) {
    clearTimeout(undock);
    homeY = window.scrollY;
    root.style.setProperty('--ov-top', `${homeY}px`);
    root.classList.add('ov-doc');
  } else if (!open && wasOpen) {
    undock = window.setTimeout(() => { root.classList.remove('ov-doc'); root.style.removeProperty('--ov-top'); }, 600);
  }
  wasOpen = open;
};
const emit = () => {
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('lock-scroll', stack.length > 0);
    dock(stack.length > 0);
    guard(stack.length > 0 || rooms > 0);
    // a released lock never leaves the page panned sideways
    if (!stack.length && window.scrollX !== 0) window.scrollTo(0, window.scrollY);
  }
  subs.forEach((f) => f());
};
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f); }; };

export const topLayer = (): LayerId | undefined => stack[stack.length - 1]?.id;
/** closes the top layer (Esc, a click on the scrim) */
export const closeTopLayer = () => stack[stack.length - 1]?.close();
/** a click inside a lower layer brings it back: the layers above it close */
export function closeAbove(id: LayerId) {
  const i = stack.findIndex((l) => l.id === id);
  if (i >= 0) [...stack.slice(i + 1)].reverse().forEach((l) => l.close());
}

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !stack.length) return;
    e.preventDefault();
    closeTopLayer();
  });
}

/** Registers an open layer; returns whether it is the top one. */
export function useLayer(id: LayerId, open: boolean, close: () => void) {
  const closeRef = useRef(close);
  closeRef.current = close;
  // a layout effect: the layer is anchored (dock) before the frame that shows it
  useLayoutEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    stack = [...stack.filter((x) => x.id !== id), { id, close: () => closeRef.current() }];
    emit();
    return () => {
      stack = stack.filter((x) => x.id !== id);
      emit();
      // the element that opened the layer gets the focus back
      if (opener?.isConnected && !opener.closest('[inert]')) opener.focus({ preventScroll: true });
    };
  }, [id, open]);
  return useSyncExternalStore(subscribe, () => topLayer() === id, () => false);
}

const hasDetail = () => stack.some((l) => l.id === 'news' || l.id === 'sheet');
/** a detail view (news preview or side sheet) is open */
export const useDetailOpen = () => useSyncExternalStore(subscribe, hasDetail, () => false);
/** DEV/tests: the layer stack, bottom → top */
export const layerStack = () => stack.map((l) => l.id);
if (import.meta.env.DEV && typeof window !== 'undefined') (window as unknown as Record<string, unknown>).__orcaLayers = layerStack;

/** A full-screen room (Orca AI on a phone) that runs past the bottom of the screen,
 *  under Safari's toolbar: the page itself stays still — only the room's own
 *  scroll areas scroll (the same touch guard as under an open layer). */
export function useStillPage(on: boolean) {
  useEffect(() => {
    if (!on) return;
    rooms += 1;
    if (!stack.length) homeY = 0;
    guard(true);
    return () => { rooms -= 1; guard(stack.length > 0 || rooms > 0); };
  }, [on]);
}
